import { MouseWheel } from "./../../utils/MouseWheel";
import { BasicBackgroundComponent } from "./BasicBackgroundComponent";
import { Stats } from "./Stats";
import { AnimatedFlashUIComponent } from "./AnimatedFlashUIComponent";
import { BasicCustomCursor } from "./BasicCustomCursor";
import { BasicDragManager } from "./BasicDragManager";
import { FlashUIComponent } from "./FlashUIComponent";
import { BasicProperties } from "./BasicProperties";
import { CommonDialogNames } from "./CommonDialogNames";


import { EnvGlobalsHandler } from "../EnvGlobalsHandler";
import { IEnvironmentGlobals } from "../IEnvironmentGlobals";
import { BasicContextMenuController } from "../controller/contextMenuController/BasicContextMenuController";
import { BasicDialog } from "./dialogs/BasicDialog";
import { BasicStandardOkDialogProperties } from "./dialogs/BasicStandardOkDialogProperties";
import { BasicPanel } from "./panels/BasicPanel";
import { BasicIdleScreen } from "./screens/BasicIdleScreen";
import { BasicScreen } from "./screens/BasicScreen";
import { Localize } from "@goodgamestudios/localization";
import { getQualifiedClassName } from "@goodgamestudios/createjs-utilities";

import Sprite = createjs.Sprite;
import StageQuality = createjs.StageQuality;
// TODO: @HTML5 ContextMenu Management
// import ContextMenuEvent = createjs.ContextMenuEvent;
// import ContextMenuItem = createjs.ContextMenuItem;
import Event = createjs.Event;
import MouseEvent = createjs.MouseEvent;
// TODO: @HTML5 handle URLRequest class
// import URLRequest = createjs.URLRequest;
// TODO: @HTML5 handle System class
// import System = createjs.System;
import Container = createjs.Container;


import {getLogger} from "@goodgamestudios/logger";
const logger = getLogger("BasicLayoutManager");

export class BasicLayoutManager extends Container {
    public static STATE_PRELOAD_INIT: number = -1;
    public static STATE_PRELOAD_CONNECTED: number = 0;

    public static STATE_CONNECT: number = 1;
    public static STATE_LOGIN: number = 2;
    public static STATE_AVATAR_CREATION: number = 3;
    public static STATE_LOAD_ITEMS: number = 4;
    public static STATE_REGISTRATION: number = 5;
    public static STATE_STARTSCREEN: number = 6;

    public static QUALITY_HIGH: number = 0;
    public static QUALITY_MEDIUM: number = 1;
    public static QUALITY_LOW: number = 2;

    public static gameState: number = 99;

    protected static layoutManagerClass: any;
    protected static layoutManager: any;

    protected backgroundLayer: Container;
    protected _backgroundComponent: BasicBackgroundComponent;
    protected screenLayer: Container;

    protected adminLayer: Container;
    protected dialogLayer: Container;
    protected panelLayer: Container;
    public tutorialLayer: Container;
    protected tooltipLayer: Container;
    protected _mouseLayer: Container;
    protected idleHolderLayer: Container;

    protected panels: Map<string, BasicPanel>;
    protected screens: Map<string, BasicScreen>;
    protected dialogs: Map<string, BasicDialog>;

    protected _currentState: number;

    private _initialized: boolean;
    private mouseWheel: MouseWheel;

    private stats: Stats;

    private animatedFlashUIComponents: AnimatedFlashUIComponent[];

    public customCursor: BasicCustomCursor;
    public dragManager: BasicDragManager;

    private _idleScreen: BasicIdleScreen;

    constructor() {
        super();
        this._initialized = false;

        if (BasicLayoutManager.layoutManager) {
            throw new Error("Calling constructor not allowed! Use getInstance instead.");
        }
    }

    /**
     * Allow the definition of the super class that will be used to create singleton instance.
     *
     * @param value class that extends BasicLayoutManager.
     */
    public static setLayoutManagerClass(value: any): void {
        BasicLayoutManager.layoutManagerClass = value;
    }

    /**
     * returns singleton
     */
    public static getInstance(): BasicLayoutManager {
        if (this.layoutManagerClass && !this.layoutManager) {
            try {
                BasicLayoutManager.layoutManager = new BasicLayoutManager.layoutManagerClass();
            } catch (e) {
                throw new Error("LayoutManagerClass is not extending BasicLayoutManager class! " + e.message);
            }
        }

        if (!BasicLayoutManager.layoutManager) {
            throw new Error("LayoutManager is not initialized!");
        }
        return BasicLayoutManager.layoutManager;
    }

    protected onMouseUp(e: Event): void {
        if (this.dragManager) {
            this.dragManager.stopDragging();
        }
    }

    /**
     * added Eventlistener and init all layers and views
     */
    // TODO: @HTML5 BasicBackgroundComponent NOT TESTED
    public initialize(backgroundComponent: BasicBackgroundComponent): void {
        this.addEventListener(Event.ADDED_TO_STAGE, this.bindFunction(this.onAddedToStage));
        this.addEventListener(Event.ENTER_FRAME, this.bindFunction(this.onEnterFrame));

        while (this.numChildren > 0) {
            this.removeChildAt(0);
        }

        this.animatedFlashUIComponents = [];
        this.dialogs = new Map();
        this.panels = new Map();
        this.screens = new Map();
        this._currentState = 0;

        this.adminLayer = new Container();
        this.screenLayer = new Container();
        this.panelLayer = new Container();
        this.dialogLayer = new Container();
        this.tutorialLayer = new Container();
        this.tooltipLayer = new Container();
        this._mouseLayer = new Container();
        this._mouseLayer.mouseChildren = false;
        this._mouseLayer.mouseEnabled = false;

        this.backgroundLayer = new Container();
        this._backgroundComponent = backgroundComponent;

        if (this._backgroundComponent) {
            ((<Container>this._backgroundComponent.disp)).mouseEnabled = true;
            ((<Container>this._backgroundComponent.disp)).mouseChildren = true;
            this.backgroundLayer.addChild(this._backgroundComponent.disp);

            if (this._backgroundComponent.customCursor) {
                this.customCursor = this._backgroundComponent.customCursor;
                this._mouseLayer.addChild(this.customCursor.disp);
            }
        }

        this._initialized = true;
    }

    public get initialized(): boolean {
        return this._initialized;
    }

    public set state(stateId: number) {
        switch (stateId) {
            case BasicLayoutManager.STATE_LOAD_ITEMS:
                this.hideAllDialogs();
                this.hideAllPanels();
                this.showBackgroundLayer();
                this._backgroundComponent.showProgressBar();
                BasicLayoutManager.gameState = BasicLayoutManager.STATE_LOAD_ITEMS;
                break;
            case BasicLayoutManager.STATE_CONNECT:
                this.hideAllDialogs();
                this.hideAllPanels();
                this.showBackgroundLayer();
                break;
            case BasicLayoutManager.STATE_LOGIN:
                this.hideAllPanels();
                this.showBackgroundLayer();
                BasicLayoutManager.gameState = BasicLayoutManager.STATE_LOGIN;
                break;
        }
    }

    /**
     * play AnimatedDisplayObject and animatedFlashUIComponents
     */
    protected onEnterFrame(event: Event): void {
        for (let animatedUIComponent of this.animatedFlashUIComponents) {
            animatedUIComponent.onEnterFrameUpdate();
        }
    }

    /**
     * added AnimatedFlashUIComponent to play it on Enterframe
     */
    public addAnimFlashUIComponent(value: AnimatedFlashUIComponent): void {
        this.animatedFlashUIComponents.push(value);
    }

    public removeAnimFlashUIComponent(value: FlashUIComponent): void {
        for (let i: number = 0; i < this.animatedFlashUIComponents.length; i++) {
            let element: FlashUIComponent = (<FlashUIComponent>this.animatedFlashUIComponents[i]);

            if (element == value) {
                this.animatedFlashUIComponents.splice(i, 1);
            }
        }
    }

    private onAddedToStage(event: Event): void {
        logger.debug("basicLayoutManager.onAddedToStage");
        this.removeEventListener(Event.ADDED_TO_STAGE, this.bindFunction(this.onAddedToStage));
        this.addEventListener(MouseEvent.MOUSE_UP, this.bindFunction(this.onMouseUp));
        // TODO: @HTML5 initialize globals
        // this.mouseWheel = new MouseWheel(this.stage, EnvGlobalsHandler.globals.loginIsKeyBased);
    }

    // @HTML5 not supported in HTML5 but I'm leaving the functionality itself
    public changeQualityLevel(quality: number): void {
        switch (quality) {
            default:
            case BasicLayoutManager.QUALITY_HIGH:
                BasicLayoutManager.layoutManager.stage.quality = StageQuality.BEST;
                break;
            case BasicLayoutManager.QUALITY_MEDIUM:
                BasicLayoutManager.layoutManager.stage.quality = StageQuality.MEDIUM;
                break;
            case BasicLayoutManager.QUALITY_LOW:
                BasicLayoutManager.layoutManager.stage.quality = StageQuality.LOW;
                break;
        }
    }

    /**
     * Getters for saved instances
     * */
    // TODO: @HTML5 function not tested
    public getScreen(name: string): BasicScreen {
        if (name in this.screens) {
            return this.screens.get(name);
        }
        return null;
    }

    // TODO: @HTML5 function not tested
    public getPanel(panelId: any): BasicPanel {
        return this.panels.get(panelId) || null;
    }

    public getDialog(dialogKey: any): BasicDialog {
        for (let dialog of Array.from(this.dialogs.values())) {
            if (dialog && this.isDialogKey(dialog, dialogKey)) {
                return dialog;
            }
        }
        return null;
    }

    /**
     * Show dialog by name
     * If the dialog already exists in dialogs list, use the old instance, else create a new instance.
     * @param dialogId name of dialog
     * @param properties dialog properties
     * @param layer layer to display dialog on (is standard dialog layer if not supplied)
     */
    public showDialog(dialogId: any, properties: BasicProperties = null, layer: Container = null, dialogtempdisp: BasicDialog = null): void {
        let dialog: BasicDialog;

        // check layer
        if (layer == null) {
            layer = this.dialogLayer;
        }

        dialog = this.dialogs.get(dialogId);

        if (!dialog) {
            dialog = <BasicDialog>this.createFlashComponent(dialogId);
            this.dialogs.set(dialogId, dialog);
        }

        dialog.setProperties(properties);

        // make sure it's on the dialog layer
        if (dialog.disp.parent != layer) {
            layer.addChild(dialog.disp);
        }

        // bring to front
        layer.setChildIndex(dialog.disp, layer.numChildren - 1);

        dialog.show();
    }

    /**
     * Show dialog by name on admin layer (for maintenance messages, etc.)
     * @param dialogId name of dialog
     * @param properties dialog properties
     */
    public showAdminDialog(dialogId: any, properties: BasicProperties = null): void {
        this.showDialog(dialogId, properties, this.adminLayer);
    }

    public hideAllDialogs(): void {
        for (let dialog of Array.from(this.dialogs.values())) {
            if (dialog.isVisible()) {
                dialog.hide();
            }
        }
    }

    public hideDialog(dialogKey: any): void {
        for (let dialog of Array.from(this.dialogs.values())) {
            if (dialog && this.isDialogKey(dialog, dialogKey) && dialog.isVisible()) {
                dialog.hide();
            }
        }
    }

    public clearDialog(dialog: BasicDialog): void {
        for (let key of Array.from(this.dialogs.keys())) {
            if (this.dialogs.get(key) === dialog) {

                if ( dialog.isVisible() ) {
                    dialog.hide();
                }

                dialog.destroy();

                if (dialog.disp && dialog.disp.parent === this.dialogLayer) {
                    this.dialogLayer.removeChild(dialog.disp);
                }

                if (dialog.disp && dialog.disp.parent === this.adminLayer) {
                    this.adminLayer.removeChild(dialog.disp);
                }

                this.dialogs.delete(key);
                break;
            }
        }
    }

    /**
     * destroy all dialogs from dialogLayer and adminlayer
     */
    public clearAllDialogs(): void {
        for (let key of Array.from(this.dialogs.keys())) {
            let dialog: BasicDialog = (<BasicDialog>this.dialogs.get(key));

            if (!dialog) {
                continue;
            }

            dialog.hide();
            dialog.destroy();

            if (dialog.disp && dialog.disp.parent == this.dialogLayer) {
                this.dialogLayer.removeChild(dialog.disp);
            }

            if (dialog.disp && dialog.disp.parent == this.adminLayer) {
                this.adminLayer.removeChild(dialog.disp);
            }

            this.dialogs.delete(key);
        }
        this.dialogs = new Map();
    }

    /**
     * show screen by NAME and added to screenLayer
     * set properties if given
     */
    public showScreen(screenId: any, properties: BasicProperties = null): BasicScreen {
        let screen: BasicScreen;

        if (this.screens.get(screenId)) {
            screen = this.screens.get(screenId);
            screen.setProperties(properties);
        } else {
            screen = (<BasicScreen>this.createFlashComponent(screenId));
            this.screens.set(screenId, screen);
            screen.setProperties(properties);
            this.screenLayer.addChild(screen.disp);
        }
        screen.show();
        return screen;
    }

    public hideScreen(screenId: string): void {
        let screen: BasicScreen;

        if (this.screens.get(screenId)) {
            screen = this.screens.get(screenId);
            screen.hide();
        }
    }

    public hideAllScreens(): void {
        for (let screen of Array.from(this.screens.values())) {
            if (screen.isVisible()) {
                screen.hide();
            }
        }
    }

    /**
     *    set Screen in front
     */
    public onTopScreen(screenId: string): void {
        let screen: BasicScreen;

        if (this.screens.get(screenId)) {
            screen = (<BasicScreen>this.screens.get(screenId));
            this.screenLayer.setChildIndex(screen.disp, this.screenLayer.numChildren - 1);
        }
    }

    public createIdleLayer(): void {
        this.idleHolderLayer = new Container();
        BasicLayoutManager.layoutManager.addChild(this.idleHolderLayer);

        this._idleScreen = new BasicIdleScreen(this.idleHolderLayer);
    }

    /**
     * destroy all screens from screenlayer
     */
    public clearAllScreens(): void {
        for (let screen of Array.from(this.screens.values())) {
            screen.hide();
            screen.destroy();
            this.screenLayer.removeChild(screen.disp);
        }
        this.screens = new Map<any, any>();
    }

    /**
     * show panel by NAME and added to panelLayer
     * set properties if given
     */
    public showPanel(panelId: any, properties: BasicProperties = null, bringToFrontIfAlreadyInitialized: boolean = false): void {
        let panel: BasicPanel;

        if (this.panels.get(panelId)) {
            panel = (<BasicPanel>this.panels.get(panelId));
            panel.setProperties(properties);
            if (bringToFrontIfAlreadyInitialized) {
                this.panelLayer.setChildIndex(panel.disp, this.panelLayer.numChildren - 1);
            }
        } else {
            panel = (<BasicPanel>this.createFlashComponent(panelId));
            panel.disp.visible = false;
            this.panels.set(panelId, panel);
            panel.setProperties(properties);
            this.panelLayer.addChild(panel.disp);
        }

        if (!panel.isVisible()) {
            panel.show();
        }
    }

    public hidePanel(panelId: any): void {
        let panel: BasicPanel;

        if (this.panels.get(panelId)) {
            panel = (<BasicPanel>this.panels.get(panelId));

            if (panel.isVisible()) {
                panel.hide();
            }
        }
    }

    /**
     * destroy all panels from panellayer
     */
    public clearAllPanels(): void {
        for (let panel of Array.from(this.panels.values())) {
            panel.hide();
            panel.destroy();
            panel.disp.parent.removeChild(panel.disp);
        }
        this.panels = new Map<any, any>();
    }

    public hideAllPanels(): void {
        for (let panel of Array.from(this.panels.values())) {
            if (panel.isVisible()) {
                panel.hide();
            }
        }
    }

    public lockPanel(panelId: string): void {
        let panel: BasicPanel;

        if (this.panels.get(panelId)) {
            panel = (<BasicPanel>this.panels.get(panelId));
            panel.lockPanel();
        }
    }

    public unLockPanel(panelId: any): void {
        let panel: BasicPanel;

        if (this.panels.get(panelId)) {
            panel = (<BasicPanel>this.panels.get(panelId));
            panel.unLockPanel();
        }
    }

    public existsDialog(dialogClass: any): boolean {
        for (let dialog of Array.from(this.dialogs.values())) {
            if (dialog && dialog.constructor.name === dialogClass.name) {
                return true;
            }
        }
        return false;
    }

    public isPanelVisible(panelClass: any): boolean {
        for (let panel of Array.from(this.panels.values())) {
            if (panel && panel.constructor.name === panelClass.name) {
                return panel.isVisible();
            }
        }
        return false;
    }

    public isPanelVisibleById(panelName: string): boolean {
        let result: boolean = false;
        let panel: BasicPanel = this.getPanel(panelName);
        result = panel && panel.isVisible();
        return result;
    }

    public isDialogVisible(dialogClass: any): boolean {
        for (let dialog of Array.from(this.dialogs.values())) {
            if (dialog && dialog.constructor.name === dialogClass.name) {
                return dialog.isVisible();
            }
        }
        return false;
    }

    public get numVisibleDialogs(): number {
        let counter: number = 0;

        for (let dialog of Array.from(this.dialogs.values())) {
            if (dialog && dialog.isVisible()) {
                counter++;
            }
        }
        return counter;
    }

    /**
     * clear all panels and dialogs
     */
    public clearAllLayoutContent(): void {
        this.clearAllPanels();
        this.clearAllDialogs();
        this.clearAllScreens();
    }

    protected createFlashComponent(dialogId: any): FlashUIComponent {
        // ..
        return null;
    }

    // TODO: @HTML5 function not tested
    public checkWaitingAnimState(servermessage: string): void {
        for (let dialog of Array.from(this.dialogs.values())) {
            dialog.checkWaitingAnimState(servermessage);
        }

        for (let panel of Array.from(this.panels.values())) {
            panel.checkWaitingAnimState(servermessage);
        }

        for (let screen of Array.from(this.screens.values())) {
            screen.checkWaitingAnimState(servermessage);
        }
    }

    public toggleFullscreen(): void {
        let element: any = this.stage.canvas;
        let document: any = window.document;

        // @HTML5 error handling
        document.addEventListener("fullscreenerror", function(e: Event): void {
            this.showDialog(CommonDialogNames.StandardOkDialog_NAME,
                new BasicStandardOkDialogProperties(
                    Localize.text("fullscreenerror_msg_title"),
                    Localize.text("fullscreenerror_msg_copy")));
        });

        // @HTML5 use instead of StageDisplayState
        const isInFullscreen = !!document.fullscreenElement || !!document.webkitFullscreenElement || !!(document as any).mozFullScreenElement || !!(document as any).msFullscreenElement;
        if (isInFullscreen) {
            this.revertFullscreen();
        } else {
            element.requestFullscreen();
        }
    }

    /**
     * For HTML5, if document is hidden (inactive), we can't request or revert full screen
     */
    public isDocumentHidden(): boolean {
        let doc = window.document as any;
        return doc.hidden || doc.msHidden || doc.webkitHidden;
    }

    public revertFullscreen(): void {
        if (this.isDocumentHidden()) {
            return;
        }
        let doc = window.document as any;
        let isActuallyInFullscreen = !!doc.fullscreenElement || !!doc.webkitFullscreenElement || !!doc.mozFullScreenElement || !!doc.msFullscreenElement;
        let resultPromise: Promise<void> | void;
        try {
            if (isActuallyInFullscreen){
                if (doc.exitFullscreen) {
                    resultPromise = doc.exitFullscreen();
                } else if (doc.webkitExitFullscreen) {
                    resultPromise = doc.webkitExitFullscreen();
                } else if (doc.msExitFullscreen) {
                    resultPromise = doc.msExitFullscreen();
                }
                if (resultPromise && resultPromise.then) {
                    resultPromise.catch((reason) => {
                        logger.warn("exitFullscreen fails: " + reason);
                    });
                }
            }

        } catch (e) {
            logger.warn("exitFullscreen fails: " + e);
        }
    }

    // TODO: @HTML5 - performance measurement
    public showFPS(): void {
        this.stats = new Stats();
        this.adminLayer.addChild(this.stats);
    }

    // TODO: @HTML5 - performance measurement
    public hideFPS(): void {
        this.adminLayer.removeChild(this.stats);
        this.stats = null;
    }

    // TODO: @HTML5 - performance measurement
    public isFPSshown(): boolean {
        // TODO: @HTML5 - need a proper solution for conditional compilation
        /*if (process.env.DEBUG) {
            return (this.stats != null);
        }*/
        return false;
    }

    // TODO: @HTML5 - performance measurement
    public toggleFPSvisibility(): void {
        if (this.isFPSshown()) {
            this.hideFPS();
        } else {
            this.showFPS();
        }
    }

    public enableProgressbar(): void {
        this._backgroundComponent.progressBarEnabled = true;
    }

    public disableProgressbar(): void {
        this._backgroundComponent.progressBarEnabled = false;
    }

    public onEndProgressbar(): void {
        this._backgroundComponent.hideProgressBar();
    }

    public onStartProgressbar(): void {
        this._backgroundComponent.show();
        this._backgroundComponent.showProgressBar();
    }

    public get inGameState(): boolean {
        return this._currentState >= 10;
    }

    public get outGameState(): boolean {
        return this._currentState < 10;
    }

    public get currentState(): number {
        return this._currentState;
    }

    public showBackgroundLayer(): void {
        this._backgroundComponent.show();
    }

    public showServerAndClientVersion(): void {
        this._backgroundComponent.showVersion();
        BasicContextMenuController.instance.refreshVersionInfo();
        // TODO: @HTML5 ContextMenu Management
        // this.contextMenu = BasicContextMenuController.instance.ggsContextMenu;
    }

    // TODO: @HTML5 ContextMenu Management
    // public xmlVersionSelect = (event: ContextMenuEvent) => {
    public xmlVersionSelect(event: any): void {
        // System.setClipboard(((<ContextMenuItem>event.target)).caption);
    }

    // TODO: @HTML5 ContextMenu Management
    // private onVisitGGS = (event: ContextMenuEvent) => {
    private onVisitGGS(event: any): void {
        // BrowserUtil.executeNavigateToURL(new URLRequest("http://www.goodgamestudios.com"), "_blank");
    }

    public hideBackgroundLayer(): void {
        this._backgroundComponent.hide();
    }

    /**
     *    the current scale Factor off the Game
     *     @return Number
     */
    public get scaleFactor(): number {
        return this._backgroundComponent.scaleFactor;
    }

    /**
     *    the current x coordinate of the  BasicBackgroundComponent
     *     usefull in games like mafia to get the x postion off view components
     *  @return Number
     */
    public get gameNullPoint(): number {
        return ((<BasicBackgroundComponent>this._backgroundComponent)).gameNullPoint;
    }

    protected get env(): IEnvironmentGlobals {
        return EnvGlobalsHandler.globals;
    }

    public get mouseLayer(): Container {
        return this._mouseLayer;
    }

    /**
     * Check if the dialog key passed is belongs to the dialog instance.
     *
     * @param dialog BasicDialog instance.
     * @param dialogKey Expecting a String or Class instance.
     * @return Boolean
     */
    private isDialogKey(dialog: BasicDialog, dialogKey: any): boolean {
        return (typeof dialogKey === "string" && dialog.constructor.name === dialogKey) || (dialogKey instanceof Object && getQualifiedClassName(dialog) === dialogKey.name);
    }
}

